import { HandlePopupRequestApiDto, PopupApiDto, PopupEventApiType } from '@b2x/storefront-api-js-client/src/dto';
import Cookies from 'js-cookie';
import React, { useRef } from 'react';
import { useExitIntent } from 'use-exit-intent';

import { analytics } from './analytics/analytics';
import { ApiRequestConfig } from './api/useApiRequest';
import { usePopupsApi } from './api/usePopupsApi';
import { PopupContentTypeV2 } from './contentTypes';
import { cookies } from './cookies';
import { ModalButtonProps } from './Modal';
import { useModalsContext } from './ModalsContext';
import { PopupBuilder } from './popup/PopupBuilder';
import {
  addStoredMinimizedPopup,
  getStoredMinimizedPopups,
  popupBuilderButtonToModalButtonProps,
  removeStoredMinimizedPopup,
} from './popup/popupHelper';
import { useLocation } from './router/router';
import { getClientData, getSessionData } from './trackVisitorData';
import { useModals } from './useModals';
import { useStable } from './util';

export const usePopups = () => {
  const { handlePopups: _handlePopups } = usePopupsApi();
  const { closeMinimizedModals, showModal } = useModals();
  const { modals } = useModalsContext();
  const location = useLocation();

  const currentModals = useRef(modals);
  currentModals.current = modals;

  const handlePopups = React.useCallback(
    (event: PopupEventApiType, options?: ApiRequestConfig) => {
      return new Promise<PopupApiDto<PopupContentTypeV2> | undefined>((resolve) => {
        const popupsToExclude: Array<string> = [];
        const allCookies = Cookies.get();
        console.log('handlePopups', 'allCookies', allCookies);
        Object.entries(allCookies).forEach(([key, value]) => {
          if (key.startsWith(cookies.getPrefixedKey('popup-'))) {
            popupsToExclude.push(value);
          }
        });

        const sessionDataStored = getSessionData();
        const clientDataStored = getClientData();
        const data: HandlePopupRequestApiDto = {
          previousSessionStartDate:
            sessionDataStored.previousSessionStartDate === sessionDataStored.sessionStartDate
              ? undefined
              : sessionDataStored.previousSessionStartDate,
          productsHistory: clientDataStored.productsHistory,
          sessionStartDate: sessionDataStored.sessionStartDate,
          // Attualmente questo valore è limitato dal massimo numero di elementi della history
          visitedPagesCount: sessionDataStored.history.length,
        };

        // Chiamo le API per sapere se c'è qualcosa da mostrare
        return _handlePopups(
          event,
          location.pathname,
          popupsToExclude,
          data,
          { populate: { content: true, offer: true } },
          options
        ).then((response) => {
          const popupToShow = response.data.at(0);
          if (popupToShow) {
            resolve(popupToShow);

            // Se il popup che devo mostrare non è quello minimizzato vuol dire che quello attualmente minimizzato non è più l'ultimo, quindi lo chiudo
            const minimizedPopupsIds = getStoredMinimizedPopups();
            const currentMinimizedPopupIds = currentModals.current
              .filter((modal) => minimizedPopupsIds.includes(modal.id))
              .map((modal) => modal.id);
            if (!currentMinimizedPopupIds.includes(popupToShow.id)) {
              // Li chiudo tutti perché ad oggi non ci sono situazioni in cui avremo più di 1 popup minimizzato, e quindi per sicurezza li chiudo tutti
              closeMinimizedModals();
            }
          } else {
            resolve(undefined);
            // Se non ci sono più popup validi da mostrare chiudo anche quelli minimizzati
            closeMinimizedModals();
          }
        });
      });

      // TODO: deve tornare la dispose che userà la useEffect come cleanup function
    },
    [_handlePopups, location.pathname, closeMinimizedModals]
  );

  const { registerHandler, updateSettings } = useExitIntent();
  const updateSettingsStable = useStable(updateSettings);

  const showPopupModal = React.useCallback(
    (popup: PopupApiDto<PopupContentTypeV2>, callback?: () => void) => {
      const buttons = (popup.content?.body.buttons
        ?.map(({ button }) => (button ? popupBuilderButtonToModalButtonProps(button, popup) : undefined))
        .filter((b) => b !== undefined) || []) as Array<ModalButtonProps>;

      showModal({
        buttons,
        children: <PopupBuilder popup={popup} />,
        className: popup.classes,
        closable: popup.closable,
        code: popup.code,
        id: popup.id,
        minimizable: popup.minimizable,
        minimizedProps: popup.content?.body.minimized,
        onClose: () => {
          analytics.events.closePopup(popup.id, popup.code);
          // Rimuovo dal localStorage quando chiudo la modale, anche se la modale non è minimizzata chiamo comunque il metodo per sicurezza
          removeStoredMinimizedPopup(popup.id);
          const hasDelayOrExitIntent = (popup.delay && popup.delay > 0) || popup.showOnExit;
          return hasDelayOrExitIntent ? undefined : callback?.();
        },
        onOpen: () => {
          console.log('handlePopups', `popup ${popup.id} aperto`);
          // Creo il cookie per non farlo vedere più, che deve durare quanto popup.maxAge
          if (popup.maxAge !== 0) {
            cookies.setString(`popup-${popup.id}`, popup.id, {
              expires: popup.maxAge ? new Date(Date.now() + popup.maxAge * 1000) : undefined,
            });
          }
          analytics.events.viewPopup(popup.id, popup.code);
        },
        size:
          popup.size === 'SMALL'
            ? 'default'
            : popup.size === 'MEDIUM'
            ? 'large'
            : popup.size === 'LARGE'
            ? 'extra-large'
            : undefined,
        title: popup.content?.body.title ?? popup.code ?? '',
      });
    },
    [showModal]
  );

  const showPopup = React.useCallback(
    (popup: PopupApiDto<PopupContentTypeV2>, callback?: () => void) => {
      const minimizedPopups = getStoredMinimizedPopups();
      // Se il popup che sto provando ad aprire era già minimizzato e può ancora essereminimizzato (non è stato modificato lato BE) vuol dire che posso aprirlo direttamente minimizzato
      if (minimizedPopups.includes(popup.id) && popup.minimizable) {
        // Sovrascrivo per forzare minimizzazione immediata
        popup.startMinimized = true;
        popup.delay = 0;
        popup.showOnExit = false;
      } else if (popup.startMinimized) {
        addStoredMinimizedPopup(popup.id);
      }

      if (((popup.delay && popup.delay > 0) || popup.showOnExit) && callback) {
        callback();
      }

      const timeoutId = window.setTimeout(() => {
        if (popup.showOnExit) {
          updateSettingsStable({
            // DOCS: https://use-exit-intent.daltonmenezes.com/docs/getting-started/overview
            desktop: {
              delayInSecondsToTrigger: 0, // Delay in secondi per inattività sul sito ( è necessario settare triggerOnIdle )
              mouseLeaveDelayInSeconds: 0, // Delay in secondi dopo il mouse out ( è necessario settare triggerOnMouseLeave )
              triggerOnIdle: !popup.showOnExit,
              triggerOnMouseLeave: popup.showOnExit,
            },
            mobile: {
              delayInSecondsToTrigger: 0, // Delay in secondi per inattività sul sito ( è necessario settare triggerOnIdle )
              triggerOnIdle: !popup.showOnExit,
            },
          });
          registerHandler({
            handler: () => showPopupModal(popup, callback),
            id: popup.id,
          });
        } else {
          showPopupModal(popup, callback);
        }
      }, (popup.delay ?? 0) * 1000);

      timeoutsIds.current.push(timeoutId);

      // Deadline in secondi che disabilita i popup attivi
      const deadline = popup.timerOnExit;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (deadline && deadline > 0) {
        window.setTimeout(() => {
          // Pulisco il timeout corrente
          clearTimeout(timeoutId);

          if (popup.showOnExit) {
            // Aggiorno i settaggi dell'exit intent e lo disabilito
            updateSettingsStable({
              // DOCS: https://use-exit-intent.daltonmenezes.com/docs/getting-started/overview
              desktop: {
                triggerOnIdle: false,
                triggerOnMouseLeave: false,
              },
              mobile: {
                triggerOnIdle: false,
              },
            });
          }

          // Aggiorno la lista dei timeout attivi
          timeoutsIds.current = timeoutsIds.current.filter((id) => id !== timeoutId);
        }, deadline * 1000);
      }
    },
    [registerHandler, showPopupModal, updateSettingsStable]
  );

  const timeoutsIds = React.useRef<Array<number>>([]);

  React.useEffect(() => {
    const clearAllTimeouts = () => {
      timeoutsIds.current.forEach((id) => clearTimeout(id));
      timeoutsIds.current = [];
    };

    return clearAllTimeouts;
  }, []);

  return { handlePopups, showPopup };
};
